import type {
  MutableRefObject,
  Dispatch,
  SetStateAction,
  ReactNode,
} from "react";
import Mention from "@tiptap/extension-mention";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { MentionNodeView } from "../MentionNodeView";
import type { RichTextEditorMentionOption } from "../../types";

export const UNICODE_WHITESPACE_PATTERN =
  "[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]";

export const UNICODE_WHITESPACE_REGEX = new RegExp(UNICODE_WHITESPACE_PATTERN);

export type MentionState = {
  open: boolean;
  items: RichTextEditorMentionOption[];
  command: ((item: RichTextEditorMentionOption) => void) | null;
  rect: DOMRect | null;
};

export type MentionCallbacks = {
  getOptions: () => RichTextEditorMentionOption[];
  onQueryChange: (query: string) => void;
  setState: Dispatch<SetStateAction<MentionState>>;
  onKeyDown: (event: KeyboardEvent) => boolean;
  getRenderHoverContent: () =>
    | ((option: RichTextEditorMentionOption) => ReactNode)
    | undefined;
};

export const createMentionExtension = (
  callbacksRef: MutableRefObject<MentionCallbacks>,
) =>
  Mention.extend({
    addNodeView() {
      return ReactNodeViewRenderer((props: NodeViewProps) => (
        <MentionNodeView
          {...props}
          renderHoverContent={callbacksRef.current.getRenderHoverContent()}
        />
      ));
    },
  }).configure({
    suggestion: {
      items: ({ query }) => {
        callbacksRef.current.onQueryChange(query);
        return callbacksRef.current
          .getOptions()
          .filter((u) => u.label.toLowerCase().startsWith(query.toLowerCase()))
          .slice(0, 5);
      },
      render: () => ({
        onStart: (props) => {
          callbacksRef.current.setState({
            open: true,
            items: props.items as RichTextEditorMentionOption[],
            command: (item) => props.command(item),
            rect: props.clientRect?.() ?? null,
          });
        },
        onUpdate: (props) => {
          callbacksRef.current.setState((prev) => ({
            ...prev,
            items: props.items as RichTextEditorMentionOption[],
            command: (item) => props.command(item),
            rect: props.clientRect?.() ?? prev.rect,
          }));
        },
        onKeyDown: ({ event }) => {
          if (event.key === "Escape") {
            callbacksRef.current.setState((prev) => ({
              ...prev,
              open: false,
            }));
            return true;
          }
          return callbacksRef.current.onKeyDown(event);
        },
        onExit: () => {
          callbacksRef.current.setState((prev) => ({ ...prev, open: false }));
        },
      }),
    },
  });
